import { useState, useEffect, useCallback } from 'react';
import { api, ApiError } from '../lib/api';
import { DataTable, type Column } from '../components/DataTable';
import { certificatesApi, fieldLabel, type Certificate, type CertificateTypeInfo } from '../lib/certificatesApi';
import type { StudentView, Paged } from '../lib/types';

/**
 * The Document Engine surface — issue, list, print and revoke certificates. Admin-gated.
 * The issue form is driven entirely by the server's catalogue (type → required fields), so adding a
 * certificate type on the backend surfaces here with no frontend change.
 */
export function CertificatesPage() {
  const [tab, setTab] = useState<'issued' | 'issue'>('issued');
  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Documents</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>Certificates</h1>
        <p style={{ color: 'var(--ink-2)', fontSize: 13, marginTop: 4, maxWidth: 640 }}>
          TC, bonafide, study, conduct and more — each serial-numbered, logged, and printed on your
          letterhead. A revoked certificate keeps its number so it can never be reused.
        </p>
      </div>
      <div className="tab-strip" style={{ gap: 4, marginBottom: 16 }}>
        <TabBtn active={tab === 'issued'} onClick={() => setTab('issued')}>Issued</TabBtn>
        <TabBtn active={tab === 'issue'} onClick={() => setTab('issue')}>Issue new</TabBtn>
      </div>
      {tab === 'issued' && <IssuedTab />}
      {tab === 'issue' && <IssueTab onIssued={() => setTab('issued')} />}
    </div>
  );
}

function IssuedTab() {
  const [rows, setRows] = useState<Certificate[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [query, setQuery] = useState('');

  const load = useCallback(() => {
    // ⚠ SERVER-side paging, which this endpoint already supported.
    //
    // It previously fetched `take: 100` and paged in the browser — so a school that had issued a
    // hundred and one certificates simply could not reach the last one, and nothing on screen said
    // so. Certificates only ever accumulate, so that ceiling arrives on its own.
    certificatesApi.list({ skip: (page - 1) * size, take: size, search: query || undefined })
      .then((r) => { setRows(r.items); setTotal(r.total); })
      .catch(() => { setRows([]); setTotal(0); });
  }, [page, size, query]);
  useEffect(() => { load(); }, [load]);

  // A new search returns to page one. Searching from page 3 otherwise shows an empty result and
  // reads as "no such certificate".
  useEffect(() => { setPage(1); }, [query]);

  const print = async (id: string) => {
    setBusyId(id);
    try { await certificatesApi.openDocument(id); } catch { /* ignore */ } finally { setBusyId(null); }
  };

  const revoke = async (id: string) => {
    const reason = window.prompt('Reason for revoking this certificate:');
    if (!reason) return;
    setBusyId(id);
    try { await certificatesApi.revoke(id, reason); load(); } catch { /* ignore */ } finally { setBusyId(null); }
  };

  if (rows === null) return <p style={{ color: 'var(--ink-3)' }}>Loading…</p>;
  /**
   * Columns.
   *
   * ⚠ Inside the component: Print and Revoke call functions that live here, and `busyId` disables
   * the row currently working.
   */
  const columns: Column<typeof rows[number]>[] = [
    {
      id: 'student', header: 'Student', sortable: true, primary: true,
      // ⚠ Serial folded into the search value. Somebody holding a printed certificate has the
      // serial number in front of them — this is exactly the "find it by typing" the nursing
      // principal asked for.
      value: (c) => `${c.studentName} ${c.serialNumber}`,
      cell: (c) => (
        <>
          <div style={{ fontWeight: 600 }}>{c.studentName}</div>
          <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
            <span className="mono">{c.serialNumber}</span>
          </div>
        </>
      ),
    },
    {
      id: 'type', header: 'Type', sortable: true,
      value: (c) => c.type,
      cell: (c) => c.type,
    },
    {
      id: 'issued', header: 'Issued', sortable: true,
      // Raw ISO sorts correctly; the rendered "21/7/2026" would sort as text.
      value: (c) => c.issuedAt,
      cell: (c) => (
        <span style={{ fontSize: 13 }}>
          {new Date(c.issuedAt).toLocaleDateString('en-IN')}
        </span>
      ),
    },
    {
      id: 'status', header: 'Status', sortable: true,
      value: (c) => c.status,
      cell: (c) => (
        <div>
          <span style={{
            fontSize: 12, fontWeight: 600,
            color: c.status === 'Revoked' ? '#c62828' : 'var(--paid, #2e7d32)',
          }}>
            {c.status}
          </span>
          {/* The reason matters more than the label. A revoked certificate somebody is asking about
              needs an answer, not just a red word. */}
          {c.status === 'Revoked' && c.revokeReason && (
            <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{c.revokeReason}</div>
          )}
        </div>
      ),
    },
    {
      id: 'actions', header: '', align: 'right', actions: true,
      cell: (c) => (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn" disabled={busyId === c.id}
            onClick={(e) => { e.stopPropagation(); print(c.id); }}>
            Print
          </button>
          {/* Revoke only on an issued certificate — revoking a revoked one is not an action. */}
          {c.status === 'Issued' && (
            <button className="btn" disabled={busyId === c.id}
              onClick={(e) => { e.stopPropagation(); revoke(c.id); }}>
              Revoke
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <DataTable
      rows={rows}
      columns={columns}
      rowKey={(c) => c.id}

      search
      searchPlaceholder="Search by student name or serial number…"

      // ⚠ Search is SERVER-side, so it looks through the whole archive rather than the current page.
      // Client-side search with server-side paging is a trap: it silently searches only what happens
      // to be loaded, and a clerk concludes a certificate does not exist when it is on page 4.
      onSearch={setQuery}
      total={total}
      page={page}
      pageSize={size}
      onPage={(p, sz) => { setPage(p); setSize(sz); }}

      empty={{
        title: 'No certificates issued yet',
        body: 'Use "Issue new" to create the first one.',
      }}
    />
  );
}

function IssueTab({ onIssued }: { onIssued: () => void }) {
  const [catalogue, setCatalogue] = useState<CertificateTypeInfo[]>([]);
  const [type, setType] = useState('');
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<StudentView[]>([]);
  const [student, setStudent] = useState<StudentView | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  useEffect(() => {
    certificatesApi.catalogue().then((c) => { setCatalogue(c); if (c[0]) setType(c[0].type); }).catch(() => setCatalogue([]));
  }, []);

  // student search (debounced-ish by button)
  const doSearch = async () => {
    if (!search.trim()) return;
    try {
      const r = await api.get<Paged<StudentView>>(`/api/students?search=${encodeURIComponent(search)}&take=10`);
      setResults(r.items);
    } catch { setResults([]); }
  };

  const info = catalogue.find((c) => c.type === type);

  const issue = async () => {
    if (!student || !type) return;
    setBusy(true); setError(null); setOkMsg(null);
    try {
      const cert = await certificatesApi.issue({ type, studentId: student.id, fields });
      setOkMsg(`Issued ${cert.serialNumber}. Opening for print…`);
      await certificatesApi.openDocument(cert.id);
      // reset for the next one
      setStudent(null); setSearch(''); setResults([]); setFields({});
      setTimeout(onIssued, 800);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not issue the certificate.');
    } finally { setBusy(false); }
  };

  return (
    <div className="card" style={{ padding: 20, maxWidth: 620 }}>
      {error && <div className="pill pill-danger" style={{ padding: '8px 12px', borderRadius: 'var(--r-md)', justifyContent: 'flex-start', marginBottom: 12 }}>{error}</div>}
      {okMsg && <div className="pill pill-ok" style={{ padding: '8px 12px', borderRadius: 'var(--r-md)', justifyContent: 'flex-start', marginBottom: 12 }}>{okMsg}</div>}

      <label className="label">Certificate type</label>
      <select className="input" value={type} onChange={(e) => { setType(e.target.value); setFields({}); }} style={{ marginBottom: 16 }}>
        {catalogue.map((c) => <option key={c.type} value={c.type}>{c.label}</option>)}
      </select>

      <label className="label">Student</label>
      {student ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, padding: '8px 12px', background: 'var(--surface-2, #f6f6f4)', borderRadius: 8 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600 }}>{student.fullName}</div>
            <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{student.admissionNumber}{student.classLabel ? ` · ${student.classLabel}` : ''}</div>
          </div>
          <button className="btn" onClick={() => setStudent(null)}>Change</button>
        </div>
      ) : (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <input className="input" value={search} placeholder="Name or admission no."
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') doSearch(); }} />
            <button className="btn" onClick={doSearch}>Search</button>
          </div>
          {results.length > 0 && (
            <div style={{ marginTop: 8, border: '1px solid var(--line)', borderRadius: 8, overflow: 'hidden' }}>
              {results.map((s) => (
                <button key={s.id} onClick={() => { setStudent(s); setResults([]); }}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', border: 'none', borderBottom: '1px solid var(--line)', background: 'none', cursor: 'pointer' }}>
                  <span style={{ fontWeight: 600 }}>{s.fullName}</span>
                  <span style={{ fontSize: 12, color: 'var(--ink-3)' }}> · {s.admissionNumber}{s.classLabel ? ` · ${s.classLabel}` : ''}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {info && info.fields.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>Details for this certificate</div>
          <div style={{ display: 'grid', gap: 10 }}>
            {info.fields.map((key) => (
              <div key={key}>
                <label className="label" style={{ fontSize: 12 }}>{fieldLabel(key)}</label>
                <input className="input" value={fields[key] ?? ''}
                  onChange={(e) => setFields({ ...fields, [key]: e.target.value })} />
              </div>
            ))}
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 8 }}>
            Blank fields print as an underline you can fill by hand. What you enter here is frozen on the certificate.
          </div>
        </div>
      )}

      <button className="btn btn-accent" style={{ width: '100%', justifyContent: 'center' }}
        disabled={!student || !type || busy} onClick={issue}>
        {busy ? 'Issuing…' : 'Issue & print'}
      </button>
    </div>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{
      padding: '8px 14px', border: 'none', background: 'none', cursor: 'pointer',
      fontWeight: active ? 600 : 400, color: active ? 'var(--ink)' : 'var(--ink-3)',
      borderBottom: active ? '2px solid var(--brand)' : '2px solid transparent', marginBottom: -1,
    }}>{children}</button>
  );
}
